import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';

const { FiStar, FiChevronLeft, FiChevronRight, FiExternalLink } = FiIcons;

const GOOGLE_REVIEW_LINK = "https://maps.app.goo.gl/xxkNqBYx2ePGCw8Y8";

// Real Google Reviews from A.R. Elite Detailing's Business Profile
const reviews = [
  {
    name: "Quayde Knight",
    initials: "QK",
    rating: 5,
    text: "Best detail experience ever! Extremely friendly, flexible, and very knowledgeable…",
  },
  {
    name: "Tonya Ahnmiller",
    initials: "TA",
    rating: 5,
    text: "An amazing job was done on my vehicle… My car looks brand new!",
  },
  {
    name: "Katelyn Scott",
    initials: "KS",
    rating: 5,
    text: "Great detailing job and fair pricing… my seats look essentially new now!",
  },
  {
    name: "Jose Hernandez",
    initials: "JH",
    rating: 5,
    text: "My car look better then new… high level professional work that goes beyond any expectations.",
  },
  {
    name: "Helen Garland",
    initials: "HG",
    rating: 5,
    text: "Extremely professional and courteous service. Focused on the details…",
  },
  {
    name: "Alex Edwards",
    initials: "AE",
    rating: 5,
    text: "Dog hair was completely removed like it was never there.",
  },
  {
    name: "Jaidan Montes",
    initials: "JM",
    rating: 5,
    text: "Quality work at a great price… very convenient that this business is mobile…",
  },
  {
    name: "Bennette Walton III",
    initials: "BW",
    rating: 5,
    text: "Very professional, and detailed… it’s always top tier!",
  },
];

function StarRating({ rating }) {
  return (
    <div className="flex gap-0.5">
      {[...Array(5)].map((_, i) => (
        <FiStar 
          key={i} 
          className="text-sm"
          style={{ 
            color: i < rating ? '#F59E0B' : '#2A3B45',
            fill: i < rating ? '#F59E0B' : 'transparent'
          }} 
        />
      ))}
    </div>
  );
}

function GoogleBadge() {
  return (
    <div className="flex items-center gap-1.5 bg-[#0E1317] px-2 py-1 rounded-sm border border-[#161F26]">
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none">
        <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4"/>
        <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853"/>
        <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05"/>
        <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335"/>
      </svg>
      <span className="text-[#A1B0B8] text-[10px] font-bold uppercase tracking-wider">Google Review</span>
    </div>
  );
}

function ReviewCard({ review, isActive }) {
  return (
    <div className={`bg-[#111518] border rounded-sm p-7 flex flex-col gap-5 h-full transition-all duration-300 ${isActive ? 'border-[#2563EB]/40 shadow-[0_0_30px_rgba(37,99,235,0.05)]' : 'border-[#1A242B]'}`}>
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="w-11 h-11 rounded-full bg-gradient-to-br from-[#203038] to-[#1A242B] border border-[#2A3B45] flex items-center justify-center flex-shrink-0">
            <span className="text-[#F8F8F8] text-sm font-black">{review.initials}</span>
          </div>
          <div>
            <p className="text-[#F8F8F8] font-bold text-sm tracking-wide">{review.name}</p>
          </div>
        </div>
        <GoogleBadge />
      </div>
      
      <StarRating rating={review.rating} />
      
      <p className="text-[#A1B0B8] text-sm leading-relaxed flex-grow italic">"{review.text}"</p>
      
      <div className="pt-4 border-t border-[#161F26]">
        <span className="text-[#697C87] text-[10px] font-bold uppercase tracking-widest">Verified Customer</span>
      </div>
    </div>
  );
}

export default function ReviewsSection() {
  const [current, setCurrent] = useState(0);
  const [isPaused, setIsPaused] = useState(false);
  const intervalRef = useRef(null);
  const VISIBLE = 3;
  const total = reviews.length;

  const startAuto = () => {
    intervalRef.current = setInterval(() => {
      setCurrent(c => (c + 1) % total);
    }, 5000);
  };

  useEffect(() => {
    if (!isPaused) startAuto();
    return () => clearInterval(intervalRef.current);
  }, [isPaused]);

  const prev = () => {
    clearInterval(intervalRef.current);
    setCurrent(c => (c - 1 + total) % total);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 6000);
  };

  const next = () => {
    clearInterval(intervalRef.current);
    setCurrent(c => (c + 1) % total);
    setIsPaused(true);
    setTimeout(() => setIsPaused(false), 6000);
  };

  const getVisible = () => {
    return Array.from({ length: VISIBLE }, (_, i) => reviews[(current + i) % total]);
  };

  // Prevent the embedded preview window from crashing by forcing a safe redirect behavior
  const handleGoogleClick = (e) => {
    if (window.self !== window.top) {
      e.preventDefault();
      alert("Note: Because you are inside the preview window, Google blocks the connection (ERR_BLOCKED_BY_RESPONSE).\n\nYour link is correct and will work perfectly on the live site! We will now open it in a new tab for you.");
      window.open(GOOGLE_REVIEW_LINK, '_blank', 'noopener,noreferrer');
    }
  };

  return (
    <section className="py-24 bg-[#0A0A0A] border-b border-[#161F26] overflow-hidden">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        
        {/* Header */}
        <div className="text-center mb-14">
          <span className="text-[#2563EB] font-bold tracking-widest uppercase text-xs mb-3 block">
            Social Proof
          </span>
          <h2 className="text-3xl md:text-5xl font-black text-[#FFFFFF] uppercase tracking-tight mb-4">
            What Our Clients Are Saying About A.R. Elite Detailing
          </h2>
          <p className="text-[#A1B0B8] max-w-2xl mx-auto text-base">
            Real feedback from customers who trusted us with their vehicles.
          </p>
        </div>

        {/* Carousel — Desktop: 3 cards, Mobile: 1 card */}
        <div 
          className="relative"
          onMouseEnter={() => setIsPaused(true)}
          onMouseLeave={() => setIsPaused(false)}
        >
          {/* Desktop Grid */}
          <div className="hidden md:grid grid-cols-3 gap-6">
            <AnimatePresence mode="popLayout">
              {getVisible().map((review, i) => (
                <motion.div
                  key={`${review.name}-${current}-${i}`}
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -16 }}
                  transition={{ duration: 0.35, delay: i * 0.05 }}
                >
                  <ReviewCard review={review} isActive={i === 0} />
                </motion.div>
              ))}
            </AnimatePresence>
          </div>

          {/* Mobile Single Card */}
          <div className="md:hidden">
            <AnimatePresence mode="wait">
              <motion.div
                key={current}
                initial={{ opacity: 0, x: 40 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -40 }}
                transition={{ duration: 0.3 }}
              >
                <ReviewCard review={reviews[current]} isActive={true} />
              </motion.div>
            </AnimatePresence>
          </div>

          {/* Nav Buttons */}
          <div className="flex items-center justify-center gap-6 mt-12">
            <button 
              onClick={prev}
              className="w-10 h-10 bg-[#111518] border border-[#2A3B45] rounded-full flex items-center justify-center text-[#F8F8F8] hover:bg-[#2563EB] hover:border-[#2563EB] transition-all duration-300"
              aria-label="Previous reviews"
            >
              <FiChevronLeft className="text-lg" />
            </button>
            
            {/* Dots */}
            <div className="flex gap-2.5">
              {reviews.map((_, i) => (
                <button
                  key={i}
                  onClick={() => {
                    clearInterval(intervalRef.current);
                    setCurrent(i);
                    setIsPaused(true);
                    setTimeout(() => setIsPaused(false), 6000);
                  }}
                  aria-label={`Go to review ${i + 1}`}
                  className={`rounded-full transition-all duration-300 ${
                    i === current 
                      ? 'w-6 h-2 bg-[#2563EB]' 
                      : 'w-2 h-2 bg-[#1A242B] hover:bg-[#2A3B45]'
                  }`}
                />
              ))}
            </div>

            <button 
              onClick={next}
              className="w-10 h-10 bg-[#111518] border border-[#2A3B45] rounded-full flex items-center justify-center text-[#F8F8F8] hover:bg-[#2563EB] hover:border-[#2563EB] transition-all duration-300"
              aria-label="Next reviews"
            >
              <FiChevronRight className="text-lg" />
            </button>
          </div>
        </div>

        {/* Bottom CTA / Leave a Review */}
        <div className="mt-16 pt-12 border-t border-[#161F26] text-center max-w-2xl mx-auto">
          <div className="flex justify-center mb-6">
            <div className="flex items-center gap-3 bg-[#111518] border border-[#1A242B] px-5 py-3 rounded-sm">
              <StarRating rating={5} />
              <div className="flex flex-col text-left border-l border-[#1A242B] pl-3">
                <span className="text-[#F8F8F8] font-bold text-sm leading-none">Highly Rated</span>
                <span className="text-[#697C87] text-[10px] font-bold uppercase tracking-widest mt-0.5">32 Google Reviews</span>
              </div>
            </div>
          </div>
          
          <h3 className="text-[#FFFFFF] text-2xl font-black uppercase tracking-tight mb-3">
            Leave a Review
          </h3>
          <p className="text-[#A1B0B8] text-sm mb-8">
            Had a great experience? Let others know by leaving us a review on Google. Real customer feedback helps new clients book with confidence.
          </p>
          
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href={GOOGLE_REVIEW_LINK}
              target="_blank" 
              rel="noopener noreferrer"
              onClick={handleGoogleClick}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-8 py-4 bg-[#2563EB] text-[#F8F8F8] font-bold rounded-sm hover:bg-[#1D4ED8] transition-all duration-300 uppercase tracking-wide text-sm shadow-[0_4px_20px_rgba(37,99,235,0.3)] hover:-translate-y-0.5"
            >
              Leave a Review <FiExternalLink className="text-base" />
            </a>
            <a 
              href={GOOGLE_REVIEW_LINK}
              target="_blank" 
              rel="noopener noreferrer"
              onClick={handleGoogleClick}
              className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-4 border-2 border-[#2A3B45] text-[#F8F8F8] font-bold rounded-sm hover:border-[#2563EB] hover:text-[#3B82F6] transition-all duration-300 uppercase tracking-wide text-sm"
            >
              See More on Google
            </a>
          </div>
          <p className="text-[#697C87] text-[10px] mt-6 italic opacity-70">
            Note: If you are in the editor preview, Google may block the connection. The link will work normally when deployed.
          </p>
        </div>

      </div>
    </section>
  );
}